<script lang="ts" setup>
import { ref, computed } from 'vue'
import { ChevronLeft } from 'lucide-vue-next'
import { Separator } from 'reka-ui'
import { useUserStore } from '@/stores/userStore'
import { navigateTo } from '@/router/utils'
import { useTranslateKey } from '@/lib/utils'
import { getSidebarItems, type NavItems } from './sidebarConfig'
import SidebarDropdown from './SidebarDropdown.vue'
import SidebarSubmenu from './SidebarSubmenu.vue'
import SidebarNavItem from './SidebarNavItem.vue'
import { useRoute } from 'vue-router'
import { toast } from 'vue-sonner'

interface Props {
  embedded?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  embedded: false
})

const emit = defineEmits<{
  close: []
}>()

const { translateKey } = useTranslateKey()
const userStore = useUserStore()
const sidebarItems = getSidebarItems(userStore)

// this is for submenu handling
const showSubMenu = ref(false)
const activeMainRoute = ref<NavItems | null>(null)

// this is for selected
const route = useRoute()

const checkActive = (navItem: NavItems) => {
  return route.name === navItem.route
}

const isNavItemLocked = (n: NavItems) =>
  Boolean(
    (n.betaOnly && !userStore.isBetaAccess) || (n.fileroomsOnly && !userStore.hasFileroomsAccess)
  )

const navItemTeaserKey = (n: NavItems) =>
  n.fileroomsOnly ? 'fileroomsFeatureTeaser' : 'betaFeatureTeaser'

const resetNavbarState = () => {
  showSubMenu.value = false
  activeMainRoute.value = null
}

const navigateToRouteHandler = (navItem: NavItems) => {
  if (isNavItemLocked(navItem)) {
    toast.info(translateKey(navItemTeaserKey(navItem)))
    return
  }
  if (navItem.newsboxFeatureOnly && !userStore.isNewsboxFeatureEnabled) {
    toast.info(translateKey('betaFeatureTeaser'))
    return
  }
  if (navItem.subroutes && navItem.subroutes.length > 0) {
    if (activeMainRoute.value?.label === navItem.label) {
      resetNavbarState()
      return
    }
    showSubMenu.value = true
    activeMainRoute.value = navItem
    return
  }
  emit('close')
  if (navItem.route) {
    resetNavbarState()
    navigateTo(navItem.route)
  }
}

const handleBackClick = () => {
  showSubMenu.value = false
  activeMainRoute.value = null
}

const containerClass = computed(() => {
  if (props.embedded) {
    return showSubMenu.value ? 'w-[800px]' : 'w-[400px]'
  }
  return 'w-full'
})

defineExpose({
  resetNavbarState,
  showSubMenu
})
</script>

<template>
  <div class="flex h-full overflow-hidden transition-all duration-300" :class="containerClass">
    <div class="flex h-full w-full shrink-0 flex-col md:w-[400px]">
      <div class="flex flex-1 flex-col justify-between p-6 pt-10">
        <div class="flex flex-col gap-5">
          <template v-for="mainRoutes in sidebarItems.primary" :key="mainRoutes.label">
            <SidebarNavItem
              :nav-item="mainRoutes"
              :is-active="checkActive(mainRoutes)"
              :is-secondary-menu-item="false"
              :has-opened-submenu="activeMainRoute?.label === mainRoutes.label"
              :is-disabled="isNavItemLocked(mainRoutes)"
              :show-beta-badge="Boolean(mainRoutes.betaOnly) && !mainRoutes.addOnBadge"
              :show-add-on-badge="
                Boolean(mainRoutes.fileroomsOnly) || Boolean(mainRoutes.addOnBadge)
              "
              @click="navigateToRouteHandler(mainRoutes)"
            />
          </template>
          <Separator v-if="sidebarItems.admin.length > 0" class="bg-border w-[80%] p-[0.75px]" />
          <template v-for="adminRoutes in sidebarItems.admin" :key="adminRoutes.label">
            <SidebarNavItem
              :nav-item="adminRoutes"
              :is-active="checkActive(adminRoutes)"
              :is-secondary-menu-item="false"
              :has-opened-submenu="activeMainRoute?.label === adminRoutes.label"
              :is-disabled="isNavItemLocked(adminRoutes)"
              :show-beta-badge="Boolean(adminRoutes.betaOnly) && !adminRoutes.addOnBadge"
              :show-add-on-badge="
                Boolean(adminRoutes.fileroomsOnly) || Boolean(adminRoutes.addOnBadge)
              "
              @click="navigateToRouteHandler(adminRoutes)"
            />
          </template>
        </div>
        <div class="flex flex-col gap-4 uppercase">
          <template v-for="secondaryRoutes in sidebarItems.secondary" :key="secondaryRoutes.label">
            <SidebarNavItem
              :nav-item="secondaryRoutes"
              :is-active="checkActive(secondaryRoutes)"
              :is-secondary-menu-item="true"
              :has-opened-submenu="activeMainRoute?.label === secondaryRoutes.label"
              :is-disabled="isNavItemLocked(secondaryRoutes)"
              :show-beta-badge="Boolean(secondaryRoutes.betaOnly) && !secondaryRoutes.addOnBadge"
              :show-add-on-badge="
                Boolean(secondaryRoutes.fileroomsOnly) || Boolean(secondaryRoutes.addOnBadge)
              "
              @click="navigateToRouteHandler(secondaryRoutes)"
            />
          </template>
        </div>
      </div>
      <div class="border-border relative flex flex-col gap-3 border-t p-6">
        <div class="font-mont-blanc-regular">
          {{ userStore.user.email }}
        </div>
        <div>
          {{ userStore.user.organisation.name }}
        </div>
        <div class="absolute right-6" data-testid="sidebar-dropdown-container">
          <SidebarDropdown @navigate="emit('close')" />
        </div>
      </div>
    </div>
    <div
      v-if="embedded && showSubMenu && activeMainRoute"
      class="bg-background border-border relative h-full w-[400px] shrink-0 overflow-y-auto border-l px-6 pt-16 text-nowrap"
    >
      <div
        class="text-primary hover:text-primary/80 absolute top-4 flex cursor-pointer items-center transition-colors"
        @click="handleBackClick"
      >
        <ChevronLeft class="text-primary primary size-4" />
      </div>
      <div class="flex flex-col gap-5">
        <template v-for="subroute in activeMainRoute.subroutes" :key="subroute.label">
          <div
            class="group flex w-fit items-center gap-2"
            :class="{
              'cursor-not-allowed opacity-70': isNavItemLocked(subroute)
            }"
            :data-testid="`sidebar-nav-${subroute.label}`"
            :data-tutorial-id="`tutorial.nav.${subroute.label}`"
            :data-tutorial-label="translateKey(subroute.label)"
            data-tutorial-role="link"
            @click="navigateToRouteHandler(subroute)"
          >
            <h1 class="font-panama-regular relative flex items-center gap-2">
              <span>{{ translateKey(subroute.label) }}</span>
              <span
                v-if="subroute.betaOnly && !subroute.addOnBadge"
                class="border-border bg-muted text-muted-foreground rounded border px-1.5 py-0.5 text-[10px] font-medium tracking-wide uppercase"
              >
                {{ translateKey('betaBadge') }}
              </span>
              <span
                v-if="subroute.fileroomsOnly || subroute.addOnBadge"
                class="border-border bg-muted text-muted-foreground rounded border px-1.5 py-0.5 text-[10px] font-medium tracking-wide uppercase"
              >
                {{ translateKey('addOnBadge') }}
              </span>
              <div
                class="bg-primary absolute -bottom-1 left-0 h-0.25 w-0 transition-all duration-300 ease-out group-hover:w-full"
                :class="{ hidden: isNavItemLocked(subroute) }"
              />
            </h1>
          </div>
        </template>
      </div>
    </div>
    <SidebarSubmenu
      v-if="!embedded"
      :show="showSubMenu"
      :active-route="activeMainRoute"
      @navigate="navigateToRouteHandler"
      @back="handleBackClick"
    />
  </div>
</template>
